// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ReorderParameter } from "@hardwoods/App/Handlers/OrderHistory/Reorder";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import reorder from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/Reorder";
import translate from "@insite/client-framework/Translate";
import { OrderModel, OrderSettingsModel } from "@insite/client-framework/Types/ApiModels";
import OrderDetailPageTypeLink from "@insite/content-library/Components/OrderDetailPageTypeLink";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import HistoryContext, { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    language: state.context.session.language,
    isReordering: state.pages.orderHistory.isReordering,
    showAddToCartConfirmationDialog: getSettingsCollection(state).productSettings.showAddToCartConfirmationDialog,
});

const mapDispatchToProps = {
    reorder,
};

interface OwnProps {
    order: OrderModel;
    orderSettings: OrderSettingsModel;
    extendedStyles?: OrderSummaryCardStyles;
}

type Props = ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    OwnProps &
    HasToasterContext &
    HasHistory;

export interface OrderSummaryCardStyles {
    orderSummaryCardGridItem?: GridItemProps;
    container?: GridContainerProps;
    pageTypeLinkGridItem?: GridItemProps;
    orderNumberGridItem?: GridItemProps;
    orderNumberHeadingAndText?: SmallHeadingAndTextStyles;
    statusGridItem?: GridItemProps;
    statusHeadingAndText?: SmallHeadingAndTextStyles;
    webOrderNumberGridItem?: GridItemProps;
    webOrderNumberHeadingAndText?: SmallHeadingAndTextStyles;
    totalGridItem?: GridItemProps;
    totalHeadingAndText?: SmallHeadingAndTextStyles;
    dataTableCells?: DataTableCellBaseProps;
    dataCell?: DataTableCellProps;
    companyNameCell?: DataTableCellProps;
    reorderButtonSpinner?: LoadingSpinnerProps;
    reorderButton?: ButtonPresentationProps;
    customerPOCell?: DataTableCellProps;
    rejectedItemsWrapper?: InjectableCss;
    rejectedItems?: InjectableCss;
}

export const orderSummaryCardStyles: OrderSummaryCardStyles = {
    orderSummaryCardGridItem: { width: 12 },
    container: { gap: 5 },
    pageTypeLinkGridItem: { width: 12 },
    orderNumberGridItem: { width: 6 },
    statusGridItem: { width: 6 },
    webOrderNumberGridItem: { width: 6 },
    totalGridItem: { width: 6 },
    reorderButton: {
        sizeVariant: "small",
        typographyProps: {
            css: css`
                text-transform: uppercase;
            `,
        },
        css: css`
            padding: 0 25px;
        `,
    },
    companyNameCell: {
        css: css`
            max-width: 200px;
            white-space: pre-wrap;
        `,
    },
    customerPOCell: {
        css: css`
            white-space: pre-wrap;
        `,
    },
    rejectedItemsWrapper: {
        css: css`
            margin-top: 15px;
        `,
    },
    rejectedItems: {
        css: css`
            margin-top: 15px;
        `,
    },
};

const OrderSummaryCard: FC<Props> = ({
    language,
    isReordering,
    reorder,
    showAddToCartConfirmationDialog,
    order,
    toaster,
    orderSettings,
    extendedStyles,
    history,
}) => {
    const [styles] = React.useState(() => mergeToNew(orderSummaryCardStyles, extendedStyles));

    const orderDateDisplay = getLocalizedDateTime({
        dateTime: order.orderDate,
        language,
        options: {
            year: "numeric",
            month: "numeric",
            day: "numeric",
        },
    });
    const orderNumber = order.erpOrderNumber || order.webOrderNumber;

    const onClickReorder = () => {
        reorder({
            orderNumber,
            onSuccess: (result: any) => {
                if (!showAddToCartConfirmationDialog) {
                    return;
                }

                const rejectedItems = result.order?.orderLines?.filter((orderLine: any) => {
                    return result.cartLines.cartLines.findIndex((cartLine: any) => {
                        return orderLine.productId && orderLine.productId === cartLine.productId
                    }) < 0;
                });

                if (rejectedItems?.length === result.order?.orderLines?.length) {
                    toaster.addToast({
                        body: (
                            <StyledWrapper>
                                {siteMessage("Hardwoods_OrderHistory_CannotAddToCart_Toaster")}
                            </StyledWrapper>
                        ),
                        messageType: "danger",
                        timeoutLength: 6000,
                    });
                } else {
                    toaster.addToast({
                        body: (
                            <HistoryContext.Provider value={{ history }}>
                                <OrderDetailPageTypeLink 
                                    title={orderNumber} 
                                    orderNumber={orderNumber} 
                                    isVmiOrder={!!order.vmiLocationId}
                                    isVmiOrderDetailsPage={false}
                                />
                                &nbsp;{translate("Added to Cart")}
                                {rejectedItems && rejectedItems.length > 0 && (
                                    <StyledWrapper {...styles.rejectedItemsWrapper}>
                                        {rejectedItems.length}&nbsp;
                                        {rejectedItems.length > 1 ?
                                            siteMessage("Hardwoods_OrderHistory_CannotAddToCart_Toaster_Multiple") :
                                            siteMessage("Hardwoods_OrderHistory_CannotAddToCart_Toaster_Single")}
                                        
                                        <StyledWrapper {...styles.rejectedItems}>
                                            {rejectedItems.map((item: any) => (
                                                <StyledWrapper key={item.productErpNumber}>{translate("Item")}: {item.productErpNumber}</StyledWrapper>
                                            ))}
                                        </StyledWrapper>
                                    </StyledWrapper>
                                )}
                            </HistoryContext.Provider>
                        ),
                        messageType: "success",
                        timeoutLength: 6000,
                    });
                }
            },
        } as ReorderParameter);
    };

    return (
        <>
            <DataTableCell {...styles.dataCell}>
                {orderDateDisplay && (
                    <>
                        {order.erpOrderNumber && (
                            <OrderDetailPageTypeLink title={orderDateDisplay} orderNumber={orderNumber} />
                        )}
                        {!order.erpOrderNumber && order.webOrderNumber && (
                            <OrderDetailPageTypeLink title={orderDateDisplay} orderNumber={orderNumber} />
                        )}
                        {!order.erpOrderNumber && !order.webOrderNumber && <>{orderDateDisplay}</>}
                    </>
                )}
            </DataTableCell>
            <DataTableCell {...styles.dataCell}>
                {order.erpOrderNumber && (
                    <OrderDetailPageTypeLink title={order.erpOrderNumber} orderNumber={orderNumber} />
                )}
                {!order.erpOrderNumber && order.webOrderNumber && (
                    <OrderDetailPageTypeLink title={order.webOrderNumber} orderNumber={orderNumber} />
                )}
            </DataTableCell>
            <DataTableCell {...styles.companyNameCell}>{`${order.stCompanyName || "--"}`}</DataTableCell>
            <DataTableCell {...styles.dataCell}>{`${order.statusDisplay || "--"}`}</DataTableCell>
            <DataTableCell {...styles.customerPOCell}>{`${order.customerPO || "--"}`}</DataTableCell>
            <DataTableCell {...styles.dataCell}>{`${order.orderGrandTotalDisplay || "--"}`}</DataTableCell>
            <DataTableCell {...styles.dataCell}>
                {isReordering[orderNumber] && <LoadingSpinner {...styles.reorderButtonSpinner} />}
                {!isReordering[orderNumber] && (
                    <Button
                        disabled={Object.keys(isReordering).length > 0}
                        {...styles.reorderButton}
                        onClick={onClickReorder}
                    >
                        {translate("Reorder")}
                    </Button>
                )}
            </DataTableCell>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(OrderSummaryCard)));
